import {
	ClientPropertyBuildingDispositionEnum,
	ClientPropertyBuildingMaterialEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { optionalDateAsString } from "web_myplann/Shared/Validation/optionalDateAsString";
import { optionalPositiveInteger } from "web_myplann/Shared/Validation/optionalPositiveInteger";
import { optionalZipCode } from "web_myplann/Shared/Validation/optionalZipCode";
import { refineRequiredMinimum } from "web_myplann/Shared/Validation/refineRequiredMinimum";
import { trimmedNullable } from "web_myplann/Shared/Validation/trimmedNullable";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

const MAX_FLOORS = 999;

export const getBuildingFormObjectSchema = (t: TranslateFunction) => {
	const maxMessage = t(Resources.Portfolio.AddRealEstate.Validations.maxValue);
	const positiveIntegerMessages = {
		positiveMessage: t(
			Resources.Portfolio.AddRealEstate.Validations.positiveInteger,
		),
		maxMessage,
	};
	const maxLength = (max: number) =>
		t(Resources.Portfolio.AddRealEstate.Validations.maxLength, { max });

	return z.object({
		location: trimmedNullable(z.string().max(80, maxLength(80))),
		locationMunicipality: trimmedNullable(z.string().max(50, maxLength(50))),
		locationZipCode: optionalZipCode(
			t(Resources.Portfolio.AddRealEstate.Validations.zipFormat),
		),
		usableArea: optionalPositiveInteger(
			ValidationLimits.Amount.Max,
			positiveIntegerMessages,
		),
		floorArea: optionalPositiveInteger(
			ValidationLimits.Amount.Max,
			positiveIntegerMessages,
		),
		disposition: z.nativeEnum(ClientPropertyBuildingDispositionEnum).nullable(),
		buildingMaterial: z
			.nativeEnum(ClientPropertyBuildingMaterialEnum)
			.nullable(),
		numberOfFloors: optionalPositiveInteger(
			MAX_FLOORS,
			positiveIntegerMessages,
		),
		interiorValue: optionalPositiveInteger(
			ValidationLimits.Amount.Max,
			positiveIntegerMessages,
		),
		purchaseDate: optionalDateAsString(DateFormat.Iso),
		description: trimmedNullable(z.string().max(3000, maxLength(3000))),
		terrace: z.boolean(),
		loggia: z.boolean(),
		balcony: z.boolean(),
		garage: z.boolean(),
		cellar: z.boolean(),
		elevator: z.boolean(),
		barrierFree: z.boolean(),
		swimmingPool: z.boolean(),
		forInvestments: z.boolean(),
	});
};

export const BUILDING_REQUIRED_MINIMUM_FIELDS = [
	"location",
	"locationMunicipality",
	"locationZipCode",
	"description",
] as const;

type BuildingRequiredMinimumFields = Record<
	(typeof BUILDING_REQUIRED_MINIMUM_FIELDS)[number],
	unknown
>;

const refineBuildingRequiredMinimum = refineRequiredMinimum(
	BUILDING_REQUIRED_MINIMUM_FIELDS,
);

export const withBuildingRequiredMinimum = <T extends z.AnyZodObject>(
	schema: T,
) =>
	schema.superRefine((rawData, ctx) =>
		// Every caller passes an object schema that carries these four fields
		// (they are part of the shared building shape and never omitted).
		refineBuildingRequiredMinimum(
			rawData as BuildingRequiredMinimumFields,
			ctx,
		),
	);

export const getBuildingFormSchema = (t: TranslateFunction) =>
	withBuildingRequiredMinimum(getBuildingFormObjectSchema(t));

export type BuildingFormInput = z.input<
	ReturnType<typeof getBuildingFormSchema>
>;
export type BuildingFormOutput = z.output<
	ReturnType<typeof getBuildingFormSchema>
>;
